import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, switchMap } from 'rxjs';
import { AlertHandler } from 'src/app/core/handlers/alert-handler';
import { MessageHandler } from 'src/app/shared/handlers/message-handler';
import { PrecisionType } from 'src/app/shared/interfaces/chart.model';
import { GlobalFilterService } from 'src/app/shared/services/global-filter.service';
import { MondelezDataService } from 'src/app/shared/services/mondelez-data.service';
import { MondelezService } from 'src/app/shared/services/mondelez.service';

@Component({
  selector: 'app-map-view-dialog',
  templateUrl: './map-view-dialog.component.html',
  styleUrls: ['./map-view-dialog.component.scss'],
})
export class MapViewDialogComponent implements OnInit, OnDestroy {
  public scenarioId: number;
  public yearRange = {
    startYear: undefined,
    endYear: undefined,
  };
  public kpiData: any;
  public kpiCardValues = {
    net_revenue: {},
    final_capacity: {},
    production: {},
    avg_shifts: {},
    avg_ge: {},
    avg_wastage: {},
    line_count: {},
    headcount: {},
  };
  public selectedFilters: any;
  public tableHeader = [];
  public tableData = [];
  public masterData: any;
  public supply_mode: string = 'manufacturing';
  public cardPanelOpen = true;

  private iteration_code: number;
  private subs = new Subscription();

  constructor(
    private mondelezService: MondelezService,
    private mondelezDataService: MondelezDataService,
    private globalFilterService: GlobalFilterService,
    public dialogRef: MatDialogRef<MapViewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.globalFilterService.globalFilters.subscribe((filter) => {
        if (filter) {
          this.yearRange.startYear = filter.start_year;
          this.yearRange.endYear = filter.end_year;
        }
      })
    );
    this.subs.add(
      this.mondelezDataService.currentScenarioChanged
        .pipe(
          switchMap((res) => {
            this.scenarioId = res.scenario_id;
            this.iteration_code = res.iteration_code;
            return this.globalFilterService.mapViewFilters;
          })
        )
        .subscribe((value: any) => {
          this.selectedFilters = value;
          if (this.scenarioId) {
            this.fetchData();
            // this.setDummyKpiData();
            // this.setKpiCardValues();
            // this.setDummyTableData();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onClose(): void {
    // this.subs.unsubscribe();
    this.dialogRef.close();
  }

  fetchData(): void {
    let payload = {
      filters: {
        region: this.selectedFilters.geography,
        start_year: this.yearRange.startYear,
        end_year: this.yearRange.endYear,
        category: null,
        brand: null,
        manufacturing_network: null,
        node_code: this.selectedFilters.node_code ? [this.selectedFilters.node_code] : null,
      },
      scenario_id: this.scenarioId,
      iteration_code: this.iteration_code,
      supply_mode: this.supply_mode,
    };
    this.subs.add(
      this.mondelezService.getMapViewDialogData(payload).subscribe({
        next: (result: any) => {
          if (result && result.response === 'SUCCESS') {
            this.masterData = result;
            this.kpiData = result.headers;
            this.setKpiCardValues();
            this.initTableData();
          } else {
            AlertHandler.ErrorAlert(MessageHandler.INTERNAL_ERROR);
          }
        },
        error: (error) => {
          AlertHandler.ErrorAlert(MessageHandler.INTERNAL_ERROR);

          if (error.status === 401) {
            console.log('Close the Dialog for unauthorized access');
            this.onClose();
          }
        },
      })
    );
  }

  setKpiCardValues(): void {
    const properties = [
      { key: 'net_revenue', precision: PrecisionType.MILLIONTH },
      { key: 'final_capacity', precision: PrecisionType.MILLIONTH },
      { key: 'production', precision: PrecisionType.MILLIONTH },
      { key: 'avg_shifts', precision: PrecisionType.NONE },
      { key: 'avg_ge', precision: PrecisionType.NONE },
      { key: 'avg_wastage', precision: PrecisionType.NONE },
      { key: 'line_count', precision: PrecisionType.NONE },
      { key: 'headcount', precision: PrecisionType.NONE },
    ];
    properties.forEach((property) => {
      this.kpiCardValues[property.key]['startYear'] = this.formatNumbers(this.kpiData[property.key][0], property.precision);
      this.kpiCardValues[property.key]['endYear'] = this.formatNumbers(this.kpiData[property.key][this.kpiData[property.key].length - 1], property.precision);
      this.kpiCardValues[property.key]['percent'] =
        ((this.kpiCardValues[property.key]['endYear'] - this.kpiCardValues[property.key]['startYear']) / this.kpiCardValues[property.key]['startYear']) * 100 || 0;
    });
  }
  private formatNumbers(amt: number, precision: PrecisionType = PrecisionType.MILLIONTH) {
    if (!amt) return 0;
    let millionth = amt / precision;
    return parseFloat(millionth.toFixed(1));
  }

  initTableData(): void {
    this.setTableHeaders();
    // this.tableExpandedRows = ['Incremental Capacity (MMlbs)', 'Shift', 'GE', 'Waste', 'CAPEX ($MM)'];
    this.tableData =
      this.masterData.details.length == 0
        ? []
        : [
            {
              node_type: 'final_capacity',
              data_key: 'final_capacity',
              code: 'Capacity (MMlbs)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: true,
              level: 1,
              plants: [],
            },
            {
              node_type: 'incremental_capacity',
              data_key: 'incremental_capacity',
              code: 'Incremental Capacity (MMlbs)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: true,
              level: 1,
              plants: [],
            },
            {
              node_type: 'avg_shifts',
              data_key: 'avg_shifts',
              code: 'Shift (#)',
              precision: PrecisionType.NONE,
              isExpanded: true,
              level: 1,
              plants: [],
            },
            {
              node_type: 'avg_ge',
              data_key: 'avg_ge',
              code: 'GE (%)',
              precision: PrecisionType.NONE,
              isExpanded: true,
              level: 1,
              plants: [],
            },
            {
              node_type: 'avg_wastage',
              data_key: 'avg_wastage',
              code: 'Waste (%)',
              precision: PrecisionType.NONE,
              isExpanded: true,
              level: 1,
              plants: [],
            },
            {
              node_type: 'capex',
              data_key: 'capex',
              code: 'CAPEX ($MM)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: true,
              level: 1,
              plants: [],
            },
          ];
    const data = this.masterData.details; // To do data processing if required
    data.forEach((el, id) => {
      this.tableData.forEach((row, index) => {
        // if (el.node_type == row.node_type && row.data_key in el) {
        if (el.period.includes('parent')) {
          const periodKey = el.period.replace('_parent', '');
          row[periodKey] = this.formatNumbers(el[row.data_key], row.precision);
          row['uid'] = id + index;
        } else {
          const existingIndex = row.plants.findIndex((item) => item.code === el.im_line_code);
          if (existingIndex === -1) {
            row.plants.push({
              code: el.im_line_code,
              [el.period]: this.formatNumbers(el[row.data_key], row.precision),
              uid: id + index,
            });
          } else {
            row.plants[existingIndex][el.period] = this.formatNumbers(el[row.data_key], row.precision);
          }
        }
        // }
      });
    });
  }

  setTableHeaders(): void {
    this.tableHeader = [];
    for (let year = this.yearRange.startYear; year <= this.yearRange.endYear; year++) {
      this.tableHeader.push(String(year));
      ['Q1_', 'Q2_', 'Q3_', 'Q4_'].forEach((el) => {
        this.tableHeader.push(el + year);
      });
    }
  }

  setDummyKpiData(): void {
    this.kpiData = {
      revenue: [4925, 3929],
      capacity: [1502, 1114],
      production: [878, 725],
      avg_shift: [400, 500],
      avg_ge: [450, 650],
      avg_waste: [340, 530],
      lines: [10, 11],
      headcount: [45, 55],
    };
  }
}
